import { inject, Injectable } from '@angular/core';
import { Chart } from '@ex/module/core/modules/chart/builder';
import { SeriesOption } from '@ex/module/core/modules/chart/utils/models';
import { SeriesType } from '@ex/module/core/modules/chart/utils/SeriesType';

@Injectable({
  providedIn: 'root'
})
export class ChartHelperService {
  private chartId: number = 0;
  constructor() { }



  prepareSeries(data: any[], defaults?: Partial<SeriesOption>): SeriesOption[] {

    const categories = [...new Set<string>(data.map(item => item.category!))];
    const seriesKeys = [...new Set<string>(data.map(item => item.series!))];
    const seriesKeyColors = seriesKeys.map(key => ({
      key,
      color: data.find(item => item?.series === key)?.color
    }));
    return seriesKeyColors.map(({ key, color: defaultColor }) => {
      const modifiedData = categories.map(cat => {
        const match = data.find(
          item => item.series === key && item.category === cat
        );

        if (match) {
          const heatColor = match.color ?? defaultColor;
          return {
            ...match,
            // make sure value is numeric and always present
            value: parseFloat(match?.value.toString() ?? 0).toFixed(1),
            value1: match.value1 ?? 0,
            itemStyle: { color: heatColor },
            color: heatColor
          };
        }

        // no data point for this (series, category) → return empty placeholder
        return {
          series: key,
          category: cat,
          value: 0,
          value1: 0,
          color: defaultColor,
          itemStyle: { color: defaultColor }
        };
      })
      return {
        ...defaults,
        name: key,
        label: {
          show: true,
          formatter: (params: any) => {
            const val = parseFloat(params.value);
            return val && val !== 0 ? `${val.toFixed(1)}%` : '';
          },
          position: 'inside'
        },
        data: modifiedData,
        itemStyle: { color: defaultColor }
      };
    }) as SeriesOption[];

  }

  populateChart(chart: Chart, chartData: any[], sort = true): void {
    if (!chartData || chartData.length === 0) {
      chart.setChart([], []); // Clear the chart with empty series and categories
      chart.rawData = [];     // Ensure rawData is also cleared
      return;
    }

    const categories = Array.from(
      new Set(
        chartData.map(d => {
          const category = d.category as string;
          return category;
        })
      )
    );
    if (sort)
      categories.sort((a, b) => a.localeCompare(b));

    let seriesKeys: string[] = [];
    if (!!chartData[0].series) {
      seriesKeys = [...new Set<string>(chartData.map(item => item.series!))];
    }

    let seriesData: any[] = [];

    if (seriesKeys.length > 0) {
      seriesData = seriesKeys.map(key => ({
        name: key,
        data: chartData
          .filter(item => item.series === key).map(item => {
            const value = parseFloat(item?.value.toString() ?? 0).toFixed(1);
            return { ...item, value }

          })
      }));
    } else {
      const data = categories.map(cat => {
        const match = chartData.find(item =>
          item.category === cat
        );
        if (match.color)
          match.itemStyle = {
            color: match.color
          }
        const value = parseFloat(match?.value ?? 0).toFixed(1);
        return { ...match, value }
      })
      seriesData = [{
        name: chart.title,
        data
      }];
    }
    chart
      .setTooltip('axis', 'cross')
      .setZoom(categories.length > 10, categories.length > 10)
      .setChart(seriesData, categories);


    chart.rawData = chartData;
    chart.id = chart.id + this.chartId++;
  }

  reArrangeArray(array: any[], orderBy: any[], orderProperty: string) {
    let newArray: any = [];
    let tempArray = JSON.parse(JSON.stringify(array));
    for (let index = 0; index < orderBy.length; index++) {
      let currentKeySelections: any = tempArray.filter(x => x[orderProperty] == orderBy[index]) as any;
      newArray.push(currentKeySelections);
    }
    return newArray.flat();
  }


  circleChart(
    chart: Chart,
    value,
    width = 35, color = '#0082C6', backgroundColor = '#E6EBF8'
  ): void {

    chart.setOptions({
      series: [{
        type: 'gauge',
        min: 0,
        max: 100,
        radius: '90%',
        startAngle: 90,
        endAngle: -270,
        progress: {
          show: true, width, itemStyle: {
            color
          }
        },
        pointer: { show: false },
        axisLine: { lineStyle: { width, color: [[1, backgroundColor]] } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        title: { show: false },
        detail: {
          offsetCenter: [0, '0%'],
          formatter: (value: number) => `{main|${value.toFixed(1)}%}`,
          rich: {
            main: { fontSize: (Number(width) > 20 ? (Number(width) + 5) : (Number(width))), fontWeight: 'bold', color: '#333' },
            sub: { fontSize: 16, color: '#666' }
          }
        },
        data: [{ value, name: '' }]
      }]
    });
  }

  gaugeChart(chart: Chart, value, width = 25, color = '#0082C6', backgroundColor = '#E6EBF8'): void {
    chart.setOptions({
      series: [{
        type: 'gauge',
        min: 0,
        max: 100,
        startAngle: 190,
        endAngle: -10,
        radius: '115%',
        center: ['50%', '70%'],
        progress: {
          show: true, width, itemStyle: {
            color
          }
        },
        pointer: { show: false },
        axisLine: { lineStyle: { width, color: [[1, backgroundColor]] } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        title: { show: false },
        detail: {
          offsetCenter: [0, '-15%'],
          formatter: (value: number) => `{main|${value.toFixed(1)}%}`,
          rich: {
            main: { fontSize: (Number(width) > 25 ? (Number(width) + 5) : (Number(width))), fontWeight: 'bold', color: '#333' },
            sub: { fontSize: 16, color: '#666' }
          }
        },
        data: [{ value, name: '' }]
      }]
    });
  }



  addCenterLabel(chartOptions: Chart, topText: string, bottomText?: string): void {
    chartOptions.updateOptions({
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: bottomText ? '44%' : '47%', // vertically shifted up
          style: {
            text: topText,
            textAlign: 'center',
            fill: 'var(--Primary-Default, #43474F)',
            fontFamily: 'Rubik',
            fontSize: 24,
            fontStyle: 'normal',
            fontWeight: 600,
          }
        },
        {
          type: 'text',
          left: 'center',
          top: '53%', // positioned below the first
          style: {
            text: bottomText,
            textAlign: 'center',
            fill: 'var(--Primary-Light, #747884)',
            fontFamily: 'Rubik',
            fontSize: 12,
            fontStyle: 'normal',
            fontWeight: 400,
          }
        }
      ]
    });
  }



}
